import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { Stage, Layer, Line, Rect, Circle, Star, Transformer, RegularPolygon } from 'react-konva';
import { useSigilContext } from '../../context/sigilContext';
import { TOOLS } from '../../util/constants';

const CanvasShape = ({ shape, isSelected, onSelect, onChange, tool }) => {
    const shapeRef = useRef();
    const trRef = useRef();

    useEffect(() => {
        if (isSelected && trRef.current) {
            // we need to attach transformer manually
            trRef.current.nodes([shapeRef.current]);
            trRef.current.getLayer().batchDraw();
        }
    }, [isSelected]);

    const handleDragEnd = (e) => {
        onChange({
            ...shape,
            x: e.target.x(),
            y: e.target.y(),
        });
    };

    const handleTransformEnd = () => {
        const node = shapeRef.current;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();

        const newAttrs = {
            ...shape,
            x: node.x(),
            y: node.y(),
            rotation: node.rotation(),
        };

        if (shape.tool === TOOLS.PENCIL || shape.tool === TOOLS.LINE) {
            // For pencil and lines, we save scales
            newAttrs.scaleX = scaleX;
            newAttrs.scaleY = scaleY;
        } else {
            // For geometric shapes, we reset scale and update dimensions
            node.scaleX(1);
            node.scaleY(1);

            if (shape.tool === TOOLS.RECT) {
                newAttrs.width = Math.max(5, node.width() * scaleX);
                newAttrs.height = Math.max(5, node.height() * scaleY);
            } else if (shape.tool === TOOLS.CIRCLE) {
                newAttrs.radius = Math.max(5, node.radius() * scaleX);
            } else if (shape.tool === TOOLS.STAR) {
                newAttrs.innerRadius = Math.max(2, node.innerRadius() * scaleX);
                newAttrs.outerRadius = Math.max(5, node.outerRadius() * scaleX);
            } else if (shape.tool === TOOLS.TRIANGLE) {
                newAttrs.radius = Math.max(5, node.radius() * scaleX);
            }
        }

        onChange(newAttrs);
    };

    const commonProps = {
        ref: shapeRef,
        ...shape,
        draggable: tool === TOOLS.SELECTOR,
        onClick: onSelect,
        onTap: onSelect,
        onDragEnd: handleDragEnd,
        onTransformEnd: handleTransformEnd,
        stroke: shape.color,
        strokeWidth: shape.lineWidth || 5,
        strokeScaleEnabled: false,
    };

    return (
        <React.Fragment>
            {shape.tool === TOOLS.RECT && <Rect {...commonProps} />}
            {shape.tool === TOOLS.CIRCLE && <Circle {...commonProps} />}
            {shape.tool === TOOLS.STAR && <Star {...commonProps} numPoints={5} />}
            {shape.tool === TOOLS.TRIANGLE && <RegularPolygon {...commonProps} sides={3} />}
            {shape.tool === TOOLS.LINE && (
                <Line
                    {...commonProps}
                    points={[0, 0, 100, 0]} // Relative points to x, y
                    strokeWidth={shape.lineWidth || 5}
                />
            )}
            {shape.tool === TOOLS.PENCIL && (
                <Line
                    {...commonProps}
                    points={shape.points}
                    tension={0.5}
                    lineCap="round"
                    lineJoin="round"
                    strokeWidth={shape.lineWidth || 5}
                    hitStrokeWidth={20}
                />
            )}
            {isSelected && (
                <Transformer
                    ref={trRef}
                    boundBoxFunc={(oldBox, newBox) => {
                        // limit resize
                        if (newBox.width < 5 || newBox.height < 5) {
                            return oldBox;
                        }
                        return newBox;
                    }}
                />
            )}
        </React.Fragment>
    );
};

const Canvas = ({ stageRefs, readOnly = false }) => {
    const {
        tool,
        setTool,
        color,
        shapes,
        setShapes,
        lineWidth,
        pushToHistory
    } = useSigilContext()
    const [isDrawing, setIsDrawing] = useState(false);
    const [selectedShapeIndex, setSelectedShapeIndex] = useState(null);
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth,
        height: 500
    });
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        const stageContainer = containerRef.current;
        if (!stageContainer) return;

        const measure = () => {
            const { offsetWidth, offsetHeight } = stageContainer;
            if (offsetWidth > 0 && offsetHeight > 0) {
                setDimensions({
                    width: offsetWidth,
                    height: offsetHeight,
                });
            }
        };

        measure();

        const observer = new ResizeObserver(() => {
            measure();
        });

        observer.observe(stageContainer);
        return () => observer.disconnect();
    }, []);

    const handleMouseDown = (e) => {
        if (readOnly) return;
        
        // Deselect when clicking on empty area
        const clickedOnEmpty = e.target === e.target.getStage().findOne('.bg-rect') || e.target === e.target.getStage();
        if (clickedOnEmpty) {
            setSelectedShapeIndex(null);
        }

        const stage = e.currentTarget;
        const pos = stage.getPointerPosition();
        if (!pos) return;

        if (tool === TOOLS.SELECTOR) {
            return;
        }

        if (tool === TOOLS.ERASE) {
            return;
        }

        // Save current shapes to history before any action
        pushToHistory(shapes);

        if (tool === TOOLS.PENCIL) {
            setIsDrawing(true);
            setShapes([...shapes, { tool, points: [pos.x, pos.y], color, lineWidth, x: 0, y: 0 }]);
        } else if (tool === TOOLS.RECT) {
            const newRect = {
                tool: TOOLS.RECT,
                x: pos.x - 50,
                y: pos.y - 50,
                width: 100,
                height: 100,
                color,
                lineWidth,
                id: `rect-${Date.now()}`
            };
            setShapes([...shapes, newRect]);
            setTool(TOOLS.SELECTOR);
            setSelectedShapeIndex(shapes.length);
        } else if (tool === TOOLS.CIRCLE) {
            const newCircle = {
                tool: TOOLS.CIRCLE,
                x: pos.x,
                y: pos.y,
                radius: 50,
                color,
                lineWidth,
                id: `circle-${Date.now()}`
            };
            setShapes([...shapes, newCircle]);
            setTool(TOOLS.SELECTOR);
            setSelectedShapeIndex(shapes.length);
        } else if (tool === TOOLS.STAR) {
            const newStar = {
                tool: TOOLS.STAR,
                x: pos.x,
                y: pos.y,
                innerRadius: 20,
                outerRadius: 50,
                color,
                lineWidth,
                id: `star-${Date.now()}`
            };
            setShapes([...shapes, newStar]);
            setTool(TOOLS.SELECTOR);
            setSelectedShapeIndex(shapes.length);
        } else if (tool === TOOLS.TRIANGLE) {
            const newTriangle = {
                tool: TOOLS.TRIANGLE,
                x: pos.x,
                y: pos.y,
                radius: 50,
                color,
                lineWidth,
                id: `triangle-${Date.now()}`
            };
            setShapes([...shapes, newTriangle]);
            setTool(TOOLS.SELECTOR);
            setSelectedShapeIndex(shapes.length);
        } else if (tool === TOOLS.LINE) {
            const newLine = {
                tool: TOOLS.LINE,
                x: pos.x - 50,
                y: pos.y,
                color,
                lineWidth,
                id: `line-${Date.now()}`
            };
            setShapes([...shapes, newLine]);
            setTool(TOOLS.SELECTOR);
            setSelectedShapeIndex(shapes.length);
        }
    }

    const handleMouseMove = (e) => {
        if (readOnly || !isDrawing || tool !== TOOLS.PENCIL) return;
        
        const stage = e.currentTarget;
        const pos = stage.getPointerPosition();
        if (!pos) return;

        const lastShape = shapes[shapes.length - 1];
        lastShape.points = lastShape.points.concat([pos.x, pos.y]);
        shapes.splice(shapes.length - 1, 1, lastShape);
        setShapes([...shapes]);
    }

    const handleMouseUp = () => {
        if (readOnly) return;
        setIsDrawing(false);
    };

    const handleShapeChange = (index, newAttrs) => {
        const updatedShapes = shapes.slice();
        updatedShapes[index] = newAttrs;
        setShapes(updatedShapes);
    };

    const handleSelectShape = (index) => {
        if (tool === TOOLS.SELECTOR) {
            setSelectedShapeIndex(index);
        } else if (tool === TOOLS.ERASE) {
            pushToHistory(shapes);
            const updatedShapes = shapes.filter((_, i) => i !== index);
            setShapes(updatedShapes);
            setSelectedShapeIndex(null);
        }
    };

    return (
        <div
            ref={containerRef}
            style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#2C2C2C',
                borderRadius: 'inherit',
                overflow: 'hidden'
            }}
        >
            <Stage
                width={dimensions.width}
                height={dimensions.height}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onTouchStart={handleMouseDown}
                onTouchMove={handleMouseMove}
                onTouchEnd={handleMouseUp}
                ref={stageRefs}
                style={{ backgroundColor: '#2C2C2C' }}
            >
                <Layer>
                    <Rect
                        name="bg-rect"
                        x={0}
                        y={0}
                        width={dimensions.width}
                        height={dimensions.height}
                        fill="#2C2C2C"
                    />
                </Layer>
                <Layer>
                    {shapes.map((shape, i) => (
                        <CanvasShape
                            key={shape.id || i}
                            shape={shape}
                            isSelected={i === selectedShapeIndex}
                            onSelect={() => handleSelectShape(i)}
                            onChange={(newAttrs) => handleShapeChange(i, newAttrs)}
                            tool={tool}
                        />
                    ))}
                </Layer>
            </Stage>
        </div>
    );
};

export default Canvas;

